import React, {useEffect, useState} from "react";
import {Card, Box, CardContent, TextField, Grid, Typography, Button, CircularProgress, makeStyles, createMuiTheme} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Title } from "react-admin";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import API from "../../helpers/apiHelpers";
import { timezones } from "../../Constants/timezones";
import moment from "moment";
import { useSnackbar } from "notistack";
import {ThemeProvider} from "@material-ui/styles";

import MomentUtils from "@date-io/moment";
import {
    colorBlack, colorError,
    colorInputBorder,
    colorInputBorderGrayish,
    colorPrimary,
    colorSecondary,
    colorWhite
} from "../../Constants/theme";

const materialTheme = createMuiTheme({
    palette: {
        common: {
            white: colorWhite,
            black: colorBlack,
        },
        primary: {
            main: colorPrimary,
        },
        secondary: {
            main: colorSecondary,
        },
        error: {
            main: colorError,
        },
        text: {
            primary: colorPrimary,
            secondary: colorInputBorder
        }
    },
    typography: {
        allVariants: {
            fontFamily: "DMSans",
            lineHeight: "26px",
            color: colorPrimary,
        },
        h2: {
            fontFamily: "Merriweather",
            fontSize: 28,
            fontWeight: "bold",
            lineHeight: "35px",
            color: colorPrimary,
        },
        h3: {
            fontWeight: "bold",
            fontSize: 24,
            color: colorPrimary,
        },
        h4: {
            fontWeight: "bold",
            fontSize: 20,
            color: colorPrimary,
        },
        body2: {
            fontSize: 16,
            fontWeight: 700,
        },
        button: {
            fontWeight: "bold",
            fontSize: 16,
            textTransform: "none",
            color: colorPrimary,
        }
    },
    overrides: {
        MuiButton: {
            root: {
                height: 24,
                color: "#fff",
                "&$disabled": {
                    backgroundColor: colorInputBorderGrayish + " !important",
                    border: "1.5px solid " + colorInputBorder,
                    borderRadius: "6px",
                },
            },
            containedPrimary: {
                color: colorPrimary,
                backgroundColor: colorSecondary,
                border: colorSecondary,
                "&:hover": {
                    color: colorWhite,
                    backgroundColor: colorSecondary,
                },
            },
            containedSecondary: {
                border: colorSecondary,
                color: colorPrimary,
                "&:hover": {
                    color: colorWhite,
                },
            },
            contained: {
                backgroundColor: colorWhite,
                color: colorInputBorder,
                border: "1.5px solid " + colorInputBorder,
                borderRadius: "6px",
                boxShadow: "none"
            }
        },
        MuiInput: {
            root: {
                borderWidth: 1
            },
        },
        MuiIconButton: {
            root: {
                padding: 0,
            },
        },
        MuiInputAdornment: {
            positionEnd: {
                marginLeft: 8,
            },
        },
        MuiOutlinedInput: {
            root: {
                "&$disabled": {
                    color: colorInputBorder,
                },
            },
            notchedOutline: {
                borderColor: colorInputBorder,
            },
            adornedEnd: {
                display: "flex",
                flexDirection: "row-reverse",
                paddingLeft: "14px",
                paddingRight: 0,
            },
        },
        MuiSelect: {
            root: {
                width: 241,
                height: 48
            }
        },
        MuiFormControlLabel: {
            label: {
                "&$disabled": {
                    color: colorInputBorder,
                }
            }
        }
    }
});

const AddVetSchedule = () => {
    const [vets, setVets] = React.useState<undefined | any>([]);
    const [vet, setVet] = React.useState<undefined | any>();
    const [timeZone, setTimeZone] = useState("UTC");
    //set initial tz
    const [queryProfile, setQueryProfile] = React.useState<string>("");
    const [startDate, setStartDate] = useState(moment.utc().startOf("hour").subtract(4, "hours"));
    const [endDate, setEndDate] = useState(moment.utc().startOf("hour"));
    const { enqueueSnackbar } = useSnackbar();

    const getProfiles = async (vetName) => {
        try {
            const profileList = await API.getProfiles({type: "VET", name: vetName});
            setVets(profileList);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (queryProfile.length) {
            const timeOutId = setTimeout(() => getProfiles(queryProfile), 500);
            return () => clearTimeout(timeOutId);
        }
    }, [queryProfile]);

    useEffect(() => {
        if (timeZone !== "") {
            moment.tz.setDefault(timeZone);
            setStartDate(moment(startDate).tz(timeZone));
            setEndDate(moment(endDate).tz(timeZone));
        }
    }, [timeZone]);

    useEffect(() => {
        if (moment(startDate).isSameOrAfter(moment(endDate))){
            enqueueSnackbar("Your end date cannot be the same or before the start date.", {
                variant: "error",
                autoHideDuration: 4000
            });
        }
    });

    const handleSubmit = async () => {
        if (!moment(startDate).isSameOrAfter(moment(endDate)) && vet && timeZone !== ""){
            const body = {
                "workDays": [
                    {
                        "profileId": vet.id,
                        "workdayBegins": moment(startDate).tz("UTC").format(),
                        "workdayEnds": moment(endDate).tz("UTC").format(),
                    }
                ]
            };
            const resp = await API.addVetSchedule(vet.id, body);
            if (resp.status !== 200){
                enqueueSnackbar("There was an error submitting the form. Please be sure all fields are entered and valid.", {
                    variant: "error",
                    autoHideDuration: 4000
                });
            } else if (resp.status === 200) {
                enqueueSnackbar("Success!", {
                    variant: "success",
                    autoHideDuration: 4000
                });
                // setVet({});
                // setStartDate(moment.utc().subtract(7, "days"));
                // setEndDate(moment.utc());
                // setTimeZone("");
            }
        }else{
            if (moment(startDate).isSameOrAfter(moment(endDate))){
                enqueueSnackbar("Your end date cannot be the same or before the start date.", {
                    variant: "error",
                    autoHideDuration: 4000
                });
            }else if (!vet) {
                enqueueSnackbar("You need to choose a vet!", {
                    variant: "error",
                    autoHideDuration: 4000
                });
            }else if (timeZone === "") {
                enqueueSnackbar("You need to choose a time zone!", {
                    variant: "error",
                    autoHideDuration: 4000
                });
            }
        }

    };

    return (
        <Card style={{maxWidth: 800}}>
            <Title title="Add Vet Availability" />
            <CardContent>
                <Typography variant="h3" style={{marginBottom: 20}}>
                    Add vet availability
                </Typography>
                <Typography>
					This form is for adding new availability for a vet so that they can schedule with a pet parent.
                </Typography>
            </CardContent>
            <>
                <CardContent>
                    <Typography variant="h6">
                Choose the Vet that you are updating.
                    </Typography>
                    <Autocomplete
                        id="vetProfile"
                        // disableClearable={true}
                        value={vet}
                        onChange={(event, value) => setVet(value)}
                        options={vets}
                        getOptionLabel={(option) => `Dr. ${option.firstName} ${option.lastName}`}
                        renderInput={(params) => <TextField {...params} variant="outlined" label="Vet" onChange={(event) => setQueryProfile(event.target.value)}/>}
                    />
                </CardContent>
                <CardContent>
                    <Typography variant="h6">
                Choose the applicable time zone.
                    </Typography>
                    <Autocomplete
                        options={timezones}
                        getOptionLabel={(option) => option}
                        value={timeZone}
                        onChange={(event, value) => {
                            if (value) {
                                setTimeZone(value);
                            }else{
                                setTimeZone("UTC");
                            }
                        }}
                        renderInput={(params) => <TextField {...params} label="Time Zone" variant="outlined" />}
                    />
                </CardContent>
                <CardContent>
                    <Typography variant="h6">
					Choose the date range.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={6} style={{paddingTop: 15}}>
                            <ThemeProvider theme={materialTheme}>
                                <DateTimePicker
                                    openTo="date"
                                    fullWidth={true}
                                    label="Start"
                                    value={startDate}
                                    inputVariant="outlined"
                                    onChange={date => setStartDate(moment(date).tz(timeZone, true))}
                                    showTodayButton
                                    minutesStep={20}
                                />
                            </ThemeProvider>
                        </Grid>
                        <Grid item xs={6} sm={6} style={{paddingTop: 15}}>
                            <ThemeProvider theme={materialTheme}>
                                <DateTimePicker
                                    openTo="date"
                                    fullWidth={true}
                                    label="End"
                                    value={endDate}
                                    inputVariant="outlined"
                                    onChange={date => setEndDate(moment(date).tz(timeZone, true))}
                                    showTodayButton
                                    minutesStep={20}
                                />
                            </ThemeProvider>
                        </Grid>
                    </Grid>
                </CardContent>
                <CardContent>
                    <Button
                        variant="contained"
                        onClick={() => handleSubmit()}
                        color="primary"
                        disabled={(timeZone != "" && vet) ? false : true}
                    >
                Submit!
                    </Button>
                </CardContent>
            </>
        </Card>
    );
};

export default AddVetSchedule;
